import React, { useState, useEffect, useRef } from 'react';
import { get, post, put } from '../../utils/apiClient';
import { clearCacheKey } from '../../utils/cacheUtils';
import Base64Image from '../Base64Image';
import {
  AdminSectionTitle,
  AdminSectionText,
  AdminLoginLabel,
  AdminLoginInput,
  AdminLoginButton,
  AdminButton,
  AdminSaveButton,
} from '../../styles/AdminPageStyles';

const AdminActsFellowshipPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);

  const [newGallery, setNewGallery] = useState({
    title: '',
    description: '',
    albumName: '',
    category: '',
    displayOrder: '',
    file: null,
    files: [],
  });
  const [uploadingOverview, setUploadingOverview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const galleryFileInputRef = useRef(null);

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await get('/acts-fellowship');
        setPageData(data);
      } catch (err) {
        setError(err && err.message ? err.message : 'Failed to load Acts Fellowship page.');
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, []);

  const handleSimpleFieldChange = (field, value) => {
    setPageData((prev) => ({
      ...(prev || {}),
      [field]: value,
    }));
  };

  const updateGalleryField = (index, field, value) => {
    setPageData((prev) => {
      if (!prev || !Array.isArray(prev.galleries)) return prev;
      const galleries = prev.galleries.map((item, i) => (
        i === index
          ? {
              ...item,
              [field]: value,
            }
          : item
      ));
      return {
        ...prev,
        galleries,
      };
    });
  };

  const handleOverviewImageUpload = async (file) => {
    if (!file) return;
    setUploadingOverview(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);
      await post('/acts-fellowship/overview/image', formData);
      const data = await get('/acts-fellowship');
      setPageData(data);
      clearCacheKey('acts_fellowship_data');
    } catch (err) {
      setUploadError(err && err.message ? err.message : 'Failed to upload overview image.');
    } finally {
      setUploadingOverview(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!pageData) return;

    setSaving(true);
    setError(null);
    setSaveSuccess(null);

    try {
      const payload = {
        header: {
          ...(pageData.header || {}),
          title:
            (pageData.header && pageData.header.title) ||
            'Acts Fellowship International',
        },
        overviewTitle: pageData.overviewTitle || '',
        overviewText: pageData.overviewText || '',
        foundedYear: pageData.foundedYear || null,
        visionText: pageData.visionText || '',
        partnershipsText: pageData.partnershipsText || '',
        galleries: Array.isArray(pageData.galleries) ? pageData.galleries : [],
      };

      const updated = await put('/acts-fellowship', payload);
      setPageData(updated);
      setSaveSuccess('Acts Fellowship page saved successfully.');
      clearCacheKey('acts_fellowship_data');
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to save Acts Fellowship page.');
    } finally {
      setSaving(false);
    }
  };

  const handleNewGalleryFieldChange = (field, value) => {
    setNewGallery((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNewGalleryFileChange = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setNewGallery((prev) => ({
      ...prev,
      file: files[0] || null,
      files,
    }));
  };

  const handleUploadGallery = async (e) => {
    e.preventDefault();
    const filesToUpload =
      newGallery.files && newGallery.files.length > 0
        ? newGallery.files
        : newGallery.file
        ? [newGallery.file]
        : [];

    if (filesToUpload.length === 0) {
      setUploadError('Please select at least one image to upload.');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append('image', file);
        if (newGallery.title) formData.append('title', newGallery.title);
        if (newGallery.description) formData.append('description', newGallery.description);
        if (newGallery.albumName) formData.append('albumName', newGallery.albumName);
        if (newGallery.category) formData.append('category', newGallery.category);
        if (newGallery.displayOrder !== '') formData.append('displayOrder', newGallery.displayOrder);

        await post('/acts-fellowship/galleries/upload', formData);
      }
      const data = await get('/acts-fellowship');
      setPageData(data);
      setNewGallery({
        title: '',
        description: '',
        albumName: '',
        category: '',
        displayOrder: '',
        file: null,
        files: [],
      });
      if (galleryFileInputRef.current) {
        galleryFileInputRef.current.value = '';
      }
      clearCacheKey('acts_fellowship_data');
    } catch (err) {
      setUploadError(err && err.message ? err.message : 'Failed to upload gallery image.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveGalleryEntry = async (index) => {
    if (!pageData || !Array.isArray(pageData.galleries)) return;
    const ok = window.confirm('Remove this gallery entry?');
    if (!ok) return;

    setSaving(true);
    setError(null);

    try {
      const nextGalleries = pageData.galleries.filter((_, i) => i !== index);
      const payload = {
        header: {
          ...(pageData.header || {}),
          title:
            (pageData.header && pageData.header.title) ||
            'Acts Fellowship International',
        },
        overviewTitle: pageData.overviewTitle || '',
        overviewText: pageData.overviewText || '',
        foundedYear: pageData.foundedYear || null,
        visionText: pageData.visionText || '',
        partnershipsText: pageData.partnershipsText || '',
        galleries: nextGalleries,
      };

      const updated = await put('/acts-fellowship', payload);
      setPageData(updated);
      clearCacheKey('acts_fellowship_data');
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to update gallery entries.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !pageData) {
    return (
      <>
        <AdminSectionTitle>Acts Fellowship CMS</AdminSectionTitle>
        <AdminSectionText>Loading Acts Fellowship page...</AdminSectionText>
      </>
    );
  }

  const galleries = pageData && Array.isArray(pageData.galleries) ? pageData.galleries : [];

  return (
    <>
      <AdminSectionTitle>Acts Fellowship CMS</AdminSectionTitle>

      <form onSubmit={handleSave} style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="overviewTitle">Overview Title</AdminLoginLabel>
          <AdminLoginInput
            id="overviewTitle"
            value={(pageData && pageData.overviewTitle) || ''}
            onChange={(e) => handleSimpleFieldChange('overviewTitle', e.target.value)}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="overviewText">Overview Text</AdminLoginLabel>
          <textarea
            id="overviewText"
            rows={3}
            value={(pageData && pageData.overviewText) || ''}
            onChange={(e) => handleSimpleFieldChange('overviewText', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Overview Image
          </AdminSectionTitle>
          {pageData && pageData.overviewImage && (
            <div style={{ marginBottom: 8 }}>
              <Base64Image
                image={pageData.overviewImage}
                alt={(pageData && pageData.overviewTitle) || 'Acts Fellowship overview'}
                style={{ width: 220, borderRadius: 6 }}
              />
            </div>
          )}
          <AdminLoginLabel htmlFor="acts-overview-image">Upload Overview Image</AdminLoginLabel>
          <input
            id="acts-overview-image"
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
              if (file) {
                handleOverviewImageUpload(file);
                e.target.value = '';
              }
            }}
            style={{ fontSize: '0.9rem' }}
          />
          {uploadingOverview && (
            <AdminSectionText style={{ fontSize: '0.85rem' }}>
              Uploading overview image...
            </AdminSectionText>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="foundedYear">Founded Year</AdminLoginLabel>
          <AdminLoginInput
            id="foundedYear"
            type="number"
            value={
              pageData && typeof pageData.foundedYear === 'number'
                ? pageData.foundedYear
                : ''
            }
            onChange={(e) => handleSimpleFieldChange('foundedYear', e.target.value === '' ? null : Number(e.target.value))}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="visionText">Vision Text</AdminLoginLabel>
          <textarea
            id="visionText"
            rows={3}
            value={(pageData && pageData.visionText) || ''}
            onChange={(e) => handleSimpleFieldChange('visionText', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="partnershipsText">Partnerships Text</AdminLoginLabel>
          <textarea
            id="partnershipsText"
            rows={3}
            value={(pageData && pageData.partnershipsText) || ''}
            onChange={(e) => handleSimpleFieldChange('partnershipsText', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>

      <hr style={{ margin: '16px 0' }} />

      <AdminSectionTitle>Upload Gallery Image</AdminSectionTitle>
      {uploadError && (
        <AdminSectionText style={{ color: '#b71c1c' }}>{uploadError}</AdminSectionText>
      )}

      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="gallery-title">Title (optional)</AdminLoginLabel>
          <AdminLoginInput
            id="gallery-title"
            value={newGallery.title}
            onChange={(e) => handleNewGalleryFieldChange('title', e.target.value)}
          />
        </div>
        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="gallery-description">Description (optional)</AdminLoginLabel>
          <textarea
            id="gallery-description"
            rows={3}
            value={newGallery.description}
            onChange={(e) => handleNewGalleryFieldChange('description', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>
        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="gallery-album">Album Name (optional)</AdminLoginLabel>
          <AdminLoginInput
            id="gallery-album"
            value={newGallery.albumName}
            onChange={(e) => handleNewGalleryFieldChange('albumName', e.target.value)}
          />
        </div>
        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="gallery-category">Category (optional)</AdminLoginLabel>
          <AdminLoginInput
            id="gallery-category"
            value={newGallery.category}
            onChange={(e) => handleNewGalleryFieldChange('category', e.target.value)}
          />
        </div>
        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="gallery-order">Display Order (optional)</AdminLoginLabel>
          <AdminLoginInput
            id="gallery-order"
            type="number"
            value={newGallery.displayOrder}
            onChange={(e) => handleNewGalleryFieldChange('displayOrder', e.target.value)}
          />
        </div>
        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="gallery-file">Image File(s)</AdminLoginLabel>
          <input
            id="gallery-file"
            type="file"
            accept="image/*"
            multiple
            onChange={handleNewGalleryFileChange}
            ref={galleryFileInputRef}
            style={{ fontSize: '0.9rem' }}
          />
        </div>

        <AdminLoginButton type="button" onClick={handleUploadGallery} disabled={uploading}>
          {uploading ? 'Uploading...' : 'Upload Gallery Image'}
        </AdminLoginButton>
      </div>

      <hr style={{ margin: '16px 0' }} />

      <AdminSectionTitle>Existing Gallery Entries</AdminSectionTitle>
      {galleries.length === 0 && (
        <AdminSectionText>No gallery entries have been uploaded yet.</AdminSectionText>
      )}

      {galleries.length > 0 && (
        <div>
          {galleries.map((item, index) => (
            <div
              key={index}
              style={{
                border: '1px solid #ddd',
                borderRadius: 8,
                padding: 10,
                marginBottom: 8,
              }}
            >
              {item.image && (
                <div style={{ marginBottom: 8 }}>
                  <Base64Image
                    image={item.image}
                    alt={item.title || 'Gallery image'}
                    style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 4 }}
                  />
                </div>
              )}
              <div style={{ marginBottom: 8 }}>
                <AdminLoginLabel htmlFor={`gallery-title-${index}`}>
                  Title
                </AdminLoginLabel>
                <AdminLoginInput
                  id={`gallery-title-${index}`}
                  value={item.title || ''}
                  onChange={(e) => updateGalleryField(index, 'title', e.target.value)}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <AdminLoginLabel htmlFor={`gallery-album-${index}`}>
                  Album Name (optional)
                </AdminLoginLabel>
                <AdminLoginInput
                  id={`gallery-album-${index}`}
                  value={item.albumName || ''}
                  onChange={(e) => updateGalleryField(index, 'albumName', e.target.value)}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <AdminLoginLabel htmlFor={`gallery-category-${index}`}>
                  Category (optional)
                </AdminLoginLabel>
                <AdminLoginInput
                  id={`gallery-category-${index}`}
                  value={item.category || ''}
                  onChange={(e) => updateGalleryField(index, 'category', e.target.value)}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <AdminLoginLabel htmlFor={`gallery-order-${index}`}>
                  Display Order (optional)
                </AdminLoginLabel>
                <AdminLoginInput
                  id={`gallery-order-${index}`}
                  type="number"
                  value={
                    typeof item.displayOrder === 'number'
                      ? item.displayOrder
                      : ''
                  }
                  onChange={(e) =>
                    updateGalleryField(
                      index,
                      'displayOrder',
                      e.target.value === '' ? undefined : Number(e.target.value),
                    )
                  }
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <AdminLoginLabel htmlFor={`gallery-description-${index}`}>
                  Description (optional)
                </AdminLoginLabel>
                <textarea
                  id={`gallery-description-${index}`}
                  rows={3}
                  value={item.description || ''}
                  onChange={(e) => updateGalleryField(index, 'description', e.target.value)}
                  style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
                />
              </div>
              <AdminButton type="button" onClick={() => handleRemoveGalleryEntry(index)}>
                Remove Entry
              </AdminButton>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: '16px' }}>
        <AdminSaveButton type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save Acts Fellowship Page'}
        </AdminSaveButton>
        {saveSuccess && (
          <span style={{ color: '#1b5e20', fontWeight: '500' }}>{saveSuccess}</span>
        )}
        {error && (
          <span style={{ color: '#b71c1c', fontWeight: '500' }}>{error}</span>
        )}
      </div>
    </form>
    </>
  );
};

export default AdminActsFellowshipPage;
